import { UserFacingError } from "../../../utils/userFacingError";

export const memberLoginEmailInUseCode = "LOGIN_EMAIL_IN_USE";
export const memberLoginAvailableCode = "AVAILABLE";

export type MemberLoginAvailabilityEvidence = {
  code: string;
  checkedLoginEmail: string;
  suggestedLoginEmail?: string | null;
};

export type MemberLoginEmailConflictDecision = "cancel" | "use-alternative";

export type MemberLoginEmailConflict = {
  checkedLoginEmail: string;
  suggestedLoginEmail: string;
};

type MemberApplicationInputWithAdministratorLogin = {
  data: {
    adminConsoleLoginEmail?: string | null;
  };
};

export const applySuggestedAdministratorLoginEmail = <
  Input extends MemberApplicationInputWithAdministratorLogin,
>(
  input: Input,
  suggestedLoginEmail: string,
): Input =>
  ({
    ...input,
    data: {
      ...input.data,
      // Do not trim or rebuild the alias. This is the exact server-checked
      // value that staff approved in the confirmation dialog.
      adminConsoleLoginEmail: suggestedLoginEmail,
    },
  }) as Input;

type MemberLoginAvailabilityRetryFlowInput<Input, Result> = {
  initialInput: Input;
  submit: (input: Input) => Promise<Result>;
  getSubmissionEvidence: (
    result: Result,
  ) => MemberLoginCollisionSubmissionEvidence;
  getInputLoginEmail: (input: Input) => string | null | undefined;
  applySuggestedLoginEmail: (
    input: Input,
    suggestedLoginEmail: string,
  ) => Input;
  requestConfirmation: (
    conflict: MemberLoginEmailConflict,
  ) => Promise<MemberLoginEmailConflictDecision>;
};

export type MemberLoginCollisionSubmissionEvidence = {
  applicationSubmittedSuccessfully: boolean;
  blockedByDuplicate: boolean;
  loginAvailability?: MemberLoginAvailabilityEvidence | null;
  member?: unknown;
  invoice?: unknown;
  addOnInvoice?: unknown;
  stripeCheckout?: unknown;
  convertedProspect?: unknown;
};

export type MemberLoginAvailabilityRetryFlowResult<Input, Result> =
  | {
      status: "cancelled";
      conflict: MemberLoginEmailConflict;
    }
  | {
      status: "completed";
      result: Result;
      submittedInput: Input;
      usedSuggestedLoginEmail: string | null;
    };

const getLoginEmailConflict = (
  availability: MemberLoginAvailabilityEvidence | null | undefined,
): MemberLoginEmailConflict | null => {
  if (availability?.code !== memberLoginEmailInUseCode) {
    return null;
  }

  if (!availability.suggestedLoginEmail) {
    throw new UserFacingError(
      "This Administrator email is already used to sign in to another Member Dashboard, and we could not find another email to suggest. Enter a different Contact Email under Member Administrator Contact in Step 2, then try again.",
    );
  }

  return {
    checkedLoginEmail: availability.checkedLoginEmail,
    // Do not normalize this value. The server checked this exact suggestion.
    suggestedLoginEmail: availability.suggestedLoginEmail,
  };
};

const normalizeLoginEmailForComparison = (loginEmail: string) =>
  loginEmail.trim().toLowerCase();

const requireCurrentLoginAvailability = ({
  availability,
  submittedLoginEmail,
}: {
  availability: MemberLoginAvailabilityEvidence | null | undefined;
  submittedLoginEmail: string | null | undefined;
}): MemberLoginAvailabilityEvidence => {
  if (!availability || !submittedLoginEmail) {
    throw new Error(
      "Chamber Pro could not confirm the current Administrator login email. Review the Administrator email and try again.",
    );
  }

  if (
    normalizeLoginEmailForComparison(availability.checkedLoginEmail) !==
    normalizeLoginEmailForComparison(submittedLoginEmail)
  ) {
    throw new Error(
      "The Administrator login availability response is out of date. Review the Administrator email and try again.",
    );
  }

  return availability;
};

const requireUncommittedLoginConflict = (
  evidence: MemberLoginCollisionSubmissionEvidence,
) => {
  if (
    evidence.member != null ||
    evidence.invoice != null ||
    evidence.addOnInvoice != null ||
    evidence.stripeCheckout != null ||
    evidence.convertedProspect != null
  ) {
    throw new Error(
      "Chamber Pro returned an inconsistent Administrator login response. Review the member list before retrying.",
    );
  }
};

/**
 * Runs one authenticated application attempt and, only with staff approval,
 * one retry with the exact server-checked alternative login. A second conflict
 * fails closed instead of opening an unbounded confirmation loop.
 */
export const runMemberLoginAvailabilityRetryFlow = async <Input, Result>({
  initialInput,
  submit,
  getSubmissionEvidence,
  getInputLoginEmail,
  applySuggestedLoginEmail,
  requestConfirmation,
}: MemberLoginAvailabilityRetryFlowInput<Input, Result>): Promise<
  MemberLoginAvailabilityRetryFlowResult<Input, Result>
> => {
  const initialResult = await submit(initialInput);
  const initialEvidence = getSubmissionEvidence(initialResult);

  // A committed replay and a duplicate result are authoritative. The login
  // may have been claimed after the original commit, but retrying either result
  // could duplicate work. Update/credential actions resolve that later state.
  if (
    initialEvidence.applicationSubmittedSuccessfully ||
    initialEvidence.blockedByDuplicate
  ) {
    return {
      status: "completed",
      result: initialResult,
      submittedInput: initialInput,
      usedSuggestedLoginEmail: null,
    };
  }

  const initialAvailability = requireCurrentLoginAvailability({
    availability: initialEvidence.loginAvailability,
    submittedLoginEmail: getInputLoginEmail(initialInput),
  });
  const conflict = getLoginEmailConflict(initialAvailability);

  if (!conflict) {
    throw new Error(
      initialAvailability.code === memberLoginAvailableCode
        ? "Chamber Pro did not confirm the membership application. Review the member list before retrying."
        : "Chamber Pro returned an unsupported Administrator login status. Review the Administrator email and try again.",
    );
  }

  requireUncommittedLoginConflict(initialEvidence);

  const decision = await requestConfirmation(conflict);
  if (decision === "cancel") {
    return { status: "cancelled", conflict };
  }

  const retryInput = applySuggestedLoginEmail(
    initialInput,
    conflict.suggestedLoginEmail,
  );
  const retryResult = await submit(retryInput);
  const retryEvidence = getSubmissionEvidence(retryResult);

  if (retryEvidence.blockedByDuplicate) {
    return {
      status: "completed",
      result: retryResult,
      submittedInput: retryInput,
      usedSuggestedLoginEmail: conflict.suggestedLoginEmail,
    };
  }

  const retryAvailability = requireCurrentLoginAvailability({
    availability: retryEvidence.loginAvailability,
    submittedLoginEmail: getInputLoginEmail(retryInput),
  });
  const retryConflict = getLoginEmailConflict(retryAvailability);

  if (retryConflict) {
    throw new UserFacingError(
      "Someone else started using the suggested email a moment ago. Enter a different Contact Email under Member Administrator Contact in Step 2, then try again.",
    );
  }

  if (retryAvailability.code !== memberLoginAvailableCode) {
    throw new Error(
      "Chamber Pro did not confirm the suggested Administrator email as available. Review the Administrator email and try again.",
    );
  }

  if (!retryEvidence.applicationSubmittedSuccessfully) {
    throw new Error(
      "Chamber Pro did not confirm the membership application. Review the member list before retrying.",
    );
  }

  return {
    status: "completed",
    result: retryResult,
    submittedInput: retryInput,
    usedSuggestedLoginEmail: conflict.suggestedLoginEmail,
  };
};
